/**
 * serialsAllocated / serialsIssued / gapRatio as FIRST-CLASS metrics, beside the latency.
 *
 * Why this is as prominent as latency: the high-water mark advances BEFORE any serial from a
 * block is vended (D4), so losing a buffer costs a GAP, never a duplicate. That safety has a
 * price — every unissued serial in a discarded block is permanently burned out of a finite,
 * regulated field. A design can post a beautiful p99 while quietly consuming ten serials for
 * every one it hands out. Showing only latency would mislead the customer into building exactly
 * that, so the gap ratio sits next to it.
 *
 * With no live backend these read `n/a`, never `0` — zero would falsely imply zero waste.
 */

import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Alert from '@cloudscape-design/components/alert';
import SpaceBetween from '@cloudscape-design/components/space-between';

function Metric({ label, value, note, testid, emphasis }) {
  const unavailable = value === 'n/a';
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <Box
        variant="h1"
        fontSize={emphasis ? 'display-l' : 'heading-xl'}
        color={unavailable ? 'text-status-inactive' : emphasis ? 'text-status-warning' : undefined}
      >
        <span data-testid={testid}>{value}</span>
      </Box>
      <Box variant="small" color="text-body-secondary">
        {note}
      </Box>
    </div>
  );
}

export default function Metrics({ metrics, onRefresh, refreshing, live }) {
  const n = (v) => (typeof v === 'number' ? v.toLocaleString() : 'n/a');
  const gap = typeof metrics.gapRatio === 'number' ? `${(metrics.gapRatio * 100).toFixed(2)}%` : 'n/a';

  /*
   * BUFFER DEPTH IS AN INSTANT, NOT A CAPACITY — AND THIS PANEL IS WHY THAT MUST BE SAID.
   *
   * This figure used to render as a bare number ("3,990") labelled "Buffer remaining". The
   * customer's technical lead read the live site during his review, saw 3,990, and wrote
   * "the buffer holds approximately 4,000 serials" into a failure-scenario analysis he
   * circulated — then derived a 48-minute drain estimate from it. He read a point-in-time
   * depth as a capacity, which is the only reading a bare number invites. Our defect, not
   * his misreading.
   *
   * So the number is now shown AGAINST the block size, and both the label and the note say
   * the reading is instantaneous.
   *
   * ⚠️ THE BLOCK SIZE IS READ FROM THE SERVICE'S OWN `features{}`, NEVER HARDCODED. A literal
   * 10,000 here would be right until the day BLOCK_SIZE changes on one function, and then this
   * panel would state a capacity the deployment does not have — the same class of defect one
   * layer down. When the service does not report it, the denominator is OMITTED and the note
   * says so, rather than assuming a value.
   *
   * ⚠️ NO DRAIN-TIME ESTIMATE IS SHOWN. "You have 24 minutes left" needs a labels-per-hour rate
   * this page is never told, so a computed countdown would be a more confident version of
   * exactly the defect above.
   */
  const blockSize = metrics.features && typeof metrics.features.blockSize === 'number'
    ? metrics.features.blockSize
    : null;
  const depth = typeof metrics.bufferRemaining === 'number' ? metrics.bufferRemaining : null;
  const bufferValue =
    depth === null ? 'n/a' : blockSize === null ? n(depth) : `${n(depth)} of ${n(blockSize)}`;
  const bufferNote =
    blockSize === null
      ? 'instantaneous depth of the current block — not a capacity. This service does not report its block size, so the depth cannot be shown against one.'
      : 'instantaneous depth of the current block — not a capacity';

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Allocated versus issued is reported as prominently as the latency, on purpose: a design can post an excellent p99 while burning many serials for each one it issues."
          actions={
            <Button iconName="refresh" onClick={onRefresh} loading={refreshing}>
              Refresh metrics
            </Button>
          }
        >
          Serial-space accounting
        </Header>
      }
    >
      <ColumnLayout columns={5} variant="text-grid">
        <Metric
          label="Serials allocated"
          value={n(metrics.serialsAllocated)}
          note="reserved via the DynamoDB high-water mark"
          testid="m-allocated"
        />
        <Metric
          label="Serials issued"
          value={n(metrics.serialsIssued)}
          note="actually handed to a caller"
          testid="m-issued"
        />
        <Metric
          label="Gap ratio"
          value={gap}
          note="(allocated − issued) / allocated"
          testid="m-gap"
          emphasis
        />
        <Metric
          label="Buffer remaining right now"
          value={bufferValue}
          note={bufferNote}
          testid="m-buffer"
        />
        <Metric
          label="Region partition"
          value={metrics.regionId || 'n/a'}
          note="disjoint range; uniqueness never relies on replication"
          testid="m-region"
        />
      </ColumnLayout>

      {!live ? (
        <Box margin={{ top: 'm' }}>
          <StatusIndicator type="info">
            Counters read <strong>n/a</strong> rather than 0 — with no live backend there is no
            allocated/issued truth to report, and 0 would falsely imply zero waste.
          </StatusIndicator>
        </Box>
      ) : null}

      {/*
        THE CEILING, STATED WHERE THE DEPTH IS READ.

        Not in an expandable section: the misreading this prevents happened while someone was
        looking at the number, so the correction has to be visible at the same moment. The
        mechanism is named rather than asserted — a reader can check PUBLISH_LUA themselves.
      */}
      <Box margin={{ top: 'm' }}>
        <Alert
          type="info"
          header="This is a depth reading, not the buffer's capacity"
          data-testid="m-buffer-ceiling"
        >
          <SpaceBetween size="xs">
            <Box variant="p">
              The buffer holds <strong>at most one block</strong>
              {blockSize === null ? '' : <> — {n(blockSize)} serials at this deployment&rsquo;s block size</>}.
              A refill does not add to what is already there: the publish path <em>overwrites</em>{' '}
              the window (<code>SET cursor</code>, <code>SET end</code>) instead of extending it, so
              the unvended tail of the previous block is abandoned rather than accumulated. Depth
              therefore cycles between one block and the low-water line and never exceeds one block.
            </Box>
            <Box variant="p">
              Read the figure above as <strong>&ldquo;how much is left in the current block at this
              instant&rdquo;</strong>, never as &ldquo;how many serials this buffer holds&rdquo;.
              Multiplying a mid-cycle depth out into a survival time understates the best case and
              overstates the worst.
            </Box>
          </SpaceBetween>
        </Alert>
      </Box>

      <Box margin={{ top: 'm' }}>
        <ExpandableSection headerText="Why the gap ratio is displayed as prominently as latency">
          <Box variant="p">
            A serial is <strong>never</strong> issued twice — gaps are acceptable, duplicates are
            not. The high-water mark in DynamoDB advances <em>before</em> any serial from a block is
            vended, so losing the buffer costs a gap, never a collision. That safety has a price:
            every unissued serial in a discarded block is permanently burned out of a finite,
            regulated field.
          </Box>
          <Box variant="p">
            So the gap ratio is the honest counterweight to a good latency number. A design can post
            an excellent p99 while consuming ten serials for every one it hands out. Showing only
            latency would mislead you into building exactly that.
          </Box>
        </ExpandableSection>
      </Box>
    </Container>
  );
}
